import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Text,
  VStack,
  Heading,
  IconButton,
  useColorModeValue,
  Spinner,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import adminApi from '../../services/adminApi';

function LogStream({ isOpen, onClose, clientId }) {
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const logEndRef = useRef(null);
  const bgColor = useColorModeValue('gray.800', 'gray.900');
  const textColor = useColorModeValue('gray.100', 'gray.50');

  useEffect(() => {
    if (!isOpen || !clientId) return;

    const fetchLogs = async () => {
      setIsLoading(true);
      try {
        const fetchedLogs = await adminApi.getClientLogs(clientId);
        setLogs(fetchedLogs);
      } catch (error) {
        console.error('Fejl ved hentning af logs:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLogs();
    const interval = setInterval(fetchLogs, 5000); // Opdater hvert 5. sekund

    return () => clearInterval(interval);
  }, [isOpen, clientId]);

  // Scroll til bunden når nye logs kommer ind
  useEffect(() => {
    if (logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs]);

  if (!isOpen) return null;

  return (
    <Box
      position="fixed"
      right="0"
      top="0"
      bottom="0"
      width="400px"
      bg={bgColor}
      color={textColor}
      p={4}
      overflowY="auto"
      boxShadow="-4px 0 6px rgba(0, 0, 0, 0.1)"
      zIndex={1000}
    >
      <VStack align="stretch" spacing={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Heading size="md">Backend logs</Heading>
          <IconButton
            icon={<CloseIcon />}
            onClick={onClose}
            variant="ghost"
            colorScheme="whiteAlpha"
            size="sm"
          />
        </Box>

        {isLoading && logs.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Spinner />
            <Text mt={2}>Henter logs...</Text>
          </Box>
        ) : (
          <VStack align="stretch" spacing={2}>
            {logs.map((log) => (
              <Box 
                key={log.id}
                p={2}
                borderRadius="md"
                bg={log.level === 'error' ? 'red.900' : 'gray.700'}
              >
                <Text fontSize="xs" color="gray.400">
                  {new Date(log.created_at).toLocaleString()}
                </Text>
                <Text fontSize="sm" fontFamily="monospace">
                  [{log.process_type}] {log.message}
                </Text>
              </Box>
            ))}
            <div ref={logEndRef} />
          </VStack>
        )}
      </VStack>
    </Box>
  );
}

export default LogStream; 